import React, { useState } from 'react';

import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useMutation, useQuery } from '@tanstack/react-query';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import TopOptions from 'src/components/shared/topOptions/topOptions';
import { getTaskMutationOptions, getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import { type NonPhysicalTask } from 'src/services/tasks/types';

import NonPhysicalTaskModal from './components/nonPhysicalTaskModal';
import NonPhysicalTaskTable, {
  type NonPhysicalTaskTableRow,
  nonPhysicalTaskToTableRow,
} from './components/nonPhysicalTaskTable';

const NonPhysicalTaskConfiguration = () => {
  const nonPhysicalTasksQuery = useQuery({
    ...getTaskQueryOptions.nonPhysicalTasks(),
    select: (tasks) =>
      tasks.map(nonPhysicalTaskToTableRow).filter((row): row is NonNullable<NonPhysicalTaskTableRow> => !!row),
  });
  const deleteNonPhysicalTask = useMutation(getTaskMutationOptions.nonPhysicalTasks.delete());

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const [taskToDelete, setTaskToDelete] = useState<NonPhysicalTaskTableRow | null>(null);
  const [selectedTask, setSelectedTask] = useState<NonPhysicalTaskTableRow | null>(null);
  const [searchField, setSearchField] = useState('');

  const onConfirmDelete = () =>
    deleteNonPhysicalTask.mutate(taskToDelete!.id, {
      onSuccess: () => {
        setIsConfirmDeleteModalOpen(false);
        setTaskToDelete(null);
        setSearchField('');
      },
    });

  const onSave = (row: NonPhysicalTask) => {
    setIsCreateModalOpen(false);
    setSearchField(row.name);
    setSelectedTask(null);
  };

  const onCancel = () => {
    setIsCreateModalOpen(false);
    setSelectedTask(null);
  };

  const onEditClick = (row: NonPhysicalTaskTableRow) => {
    setSelectedTask(row);
    setIsCreateModalOpen(true);
  };

  const onDeleteClick = (row: NonPhysicalTaskTableRow) => {
    setIsConfirmDeleteModalOpen(true);
    setTaskToDelete(row);
  };

  return (
    <Column>
      <Row spacingInset="400 400 500 none">
        <Text alignment="center">Here you can view and modify the list of non-physical tasks within RTW.</Text>
      </Row>

      <LoadingSpinnerWrapper showSpinner={nonPhysicalTasksQuery.isFetching}>
        <TopOptions
          label="Task"
          onOpen={() => setIsCreateModalOpen(true)}
          searchField={searchField}
          setSearchField={setSearchField}
        />

        {nonPhysicalTasksQuery.isSuccess ? (
          <NonPhysicalTaskTable
            tableData={nonPhysicalTasksQuery.data}
            onDelete={onDeleteClick}
            onEdit={(row) => onEditClick(row!)}
            searchField={searchField}
          />
        ) : null}
      </LoadingSpinnerWrapper>

      <NonPhysicalTaskModal
        selectedTask={selectedTask}
        isOpen={isCreateModalOpen}
        onSave={onSave}
        onCancel={onCancel}
      />

      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        setOpen={setIsConfirmDeleteModalOpen}
        onConfirm={onConfirmDelete}
        modalTitle="Confirm Delete?"
        modalDescription={`Are you sure you want to delete ${taskToDelete?.name}?`}
      />
    </Column>
  );
};

export default NonPhysicalTaskConfiguration;
